import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MembersCountCell } from '../../components/RemoveMember';
import api from '../../lib/api';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../../components/ui';
import { stripHtml } from '../../lib/format';
import { useRole } from '../../app/RoleContext';
import { handleHasMembersError } from '../../components/RemoveMember';

export default function CoursesAdminPage() {
  const navigate = useNavigate();
  const { canSeeAdmin } = useRole();
  const [creating, setCreating] = useState(false);
  // Superadmin only: publishers who can be assigned as a course's instructor.
  const instructors = useAsync(
    () => (canSeeAdmin ? api.adminListInstructors().catch(() => []) : Promise.resolve([])), [canSeeAdmin]);

  const setInstructor = async (c, personId) => {
    const who = (instructors.data || []).find((p) => p.id === personId);
    const msg = personId
      ? `Make @${who?.username} the instructor of "${c.title}"? They'll own it: it shows as their course and they manage its lessons, pricing and members.`
      : `Remove the instructor from "${c.title}"? It becomes shared — every publisher can manage it.`;
    if (!window.confirm(msg)) return;
    try { await api.adminSetCourseInstructor(c.id, personId); reload(); }
    catch (e) { alert(e?.data?.message || e.message); }
  };

  const instructorCell = (c) => {
    if (canSeeAdmin) {
      return (
        <select className="input" style={{ minWidth: 150, padding: '4px 8px' }}
          value={c.authorPersonId || ''} onChange={(e) => setInstructor(c, e.target.value || null)}>
          <option value="">— No instructor —</option>
          {(instructors.data || []).map((p) => (
            <option key={p.id} value={p.id}>@{p.username}{p.name ? ` (${p.name})` : ''}{p.admin ? ' · superadmin' : ''}</option>
          ))}
        </select>
      );
    }
    return null;
  };
  const { loading, error, data, reload } = useAsync(async () => {
    const courses = await api.adminListCourses();
    const list = Array.isArray(courses) ? courses : [];
    // lesson counts (small admin catalog → N+1 is fine)
    const counts = await Promise.all(list.map((c) => api.adminListLessons(c.id).then((ls) => (Array.isArray(ls) ? ls.length : 0)).catch(() => 0)));
    return list.map((c, i) => ({ ...c, lessonCount: counts[i] }));
  }, []);

  // Create a draft straight away and open its detail screen — no popup.
  const newCourse = async () => {
    setCreating(true);
    try {
      const c = await api.adminCreateCourse({
        title: 'Untitled course', tier: 'FREE', status: 'DRAFT', coverColor: 'teal', brand: 'compassionpractice',
      });
      navigate(`/admin/courses/${c.id}?created=1`);
    } catch (e) { alert(e.message); setCreating(false); }
  };

  const counts = useAsync(() => api.memberCounts('course').catch(() => ({})), []);

  const togglePublish = async (c) => {
    const next = c.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    try {
      await api.adminUpdateCourse(c.id, {
        title: c.title, summary: c.summary, status: next,
        coverColor: c.coverColor, brand: c.brand,
        // Access follows price (same rule as the course editor).
        priceCents: c.priceCents || 0, currency: 'usd',
        tier: (c.priceCents || 0) > 0 ? 'PRO' : 'FREE',
      });
      reload();
    } catch (e) { if (!handleHasMembersError(e, 'courses', c.id, navigate)) alert(e?.data?.message || e.message); }
  };

  return (
    <div className="page">
      <PageHead
        icon="📚"
        title="Manage your courses"
        sub="Open a course to edit its details, lessons and price. Members and invites are under Members."
        actions={<button className="btn btn-primary" disabled={creating} onClick={newCourse}>{creating ? 'Creating…' : '+ New course'}</button>}
      />
      {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
        <Section style={{ padding: 0 }}>
          {data.length === 0 ? <div style={{ padding: 18 }}><Empty>No courses yet.</Empty></div> : (
            <table className="tbl">
              <thead><tr><th>Course</th>{canSeeAdmin && <th>Instructor</th>}<th>Lessons</th><th>Price</th><th>Members</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {data.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => navigate(`/admin/courses/${c.id}`)} style={{ cursor: 'pointer' }}>
                    <td><b>{c.title}</b>{c.summary ? <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{stripHtml(c.summary)}</div> : null}</td>
                    {canSeeAdmin && (
                      <td onClick={(e) => e.stopPropagation()}>
                        {instructorCell(c)}
                        {!c.authorPersonId && (
                          <div style={{ fontSize: '0.75rem', color: '#b45309', marginTop: 4 }}>No instructor — assign one</div>
                        )}
                      </td>
                    )}
                    <td>{c.lessonCount}</td>
                    <td>{c.priceCents > 0 ? <span className="pill pro">${(c.priceCents / 100).toFixed(2)}</span> : <span className="pill free">Free</span>}</td>
                    <td><MembersCountCell count={counts.loading ? undefined : (counts.data?.[c.id] ?? 0)} tab="courses" id={c.id} /></td>
                    <td><span className="pill" style={{ background: c.status === 'PUBLISHED' ? 'var(--green-bg)' : '#f3f4f6', color: c.status === 'PUBLISHED' ? 'var(--green-text)' : 'var(--muted)' }}>{c.status}</span></td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <button className={`btn btn-sm ${c.status === 'PUBLISHED' ? '' : 'btn-primary'}`} onClick={() => togglePublish(c)}>
                        {c.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}
    </div>
  );
}
